import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  LinearProgress,
  Stack
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useSessionReport } from '../workouts/session-view/hooks/useSessionReport';
import WorkoutSessionReport from './WorkoutSessionReport';

/**
 * Status-aware loading component that shows different states
 */
const ReportLoadingState = ({ status, isGenerating }) => {
  const theme = useTheme();

  const statusMessages = {
    waiting_for_completion: 'Waiting for session to complete...',
    creating: 'Generating your personalized report...',
    processing: 'Analyzing your workout data...',
  };

  const message = statusMessages[status] || (isGenerating ? 'Generating report...' : 'Loading...');

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        p: 4, 
        minHeight: '300px',
        gap: 2
      }}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress size={60} thickness={4} />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 24 }} />
        </Box>
      </Box>
      <Typography variant="body1" color="text.secondary">
        {message}
      </Typography>
      {status === 'creating' && (
        <Box sx={{ width: '60%', mt: 1 }}>
          <LinearProgress variant="indeterminate" />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            Analyzing exercises, calculating PRs, generating insights...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

ReportLoadingState.propTypes = {
  status: PropTypes.string,
  isGenerating: PropTypes.bool,
};

/**
 * Error state with retry option
 */
const ReportErrorState = ({ error, onRetry }) => {
  const theme = useTheme();

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        p: 4, 
        minHeight: '200px',
        gap: 2
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main' }} />
      <Typography variant="h6" color="error.main">
        Unable to Load Report
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 400 }}>
        {error || 'There was a problem generating your workout report. This might be a temporary issue.'}
      </Typography>
      {onRetry && (
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          sx={{ mt: 1 }}
        >
          Try Again
        </Button>
      )}
    </Box>
  );
};

ReportErrorState.propTypes = {
  error: PropTypes.string,
  onRetry: PropTypes.func,
};

/**
 * Report status badge
 */
const ReportStatusBadge = ({ status, reportVersion }) => {
  const statusConfig = {
    complete: { label: 'Complete', color: 'success' },
    creating: { label: 'Generating...', color: 'info' },
    waiting_for_completion: { label: 'Pending', color: 'warning' },
    failed: { label: 'Failed', color: 'error' },
  };

  const config = statusConfig[status] || { label: status, color: 'default' };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Chip 
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
      {reportVersion && reportVersion >= 2 && (
        <Chip 
          icon={<AutoAwesomeIcon sx={{ fontSize: '0.9rem !important' }} />}
          label="Enhanced"
          size="small"
          variant="outlined"
          sx={{ 
            borderColor: 'primary.main',
            color: 'primary.main',
            '& .MuiChip-icon': { color: 'primary.main' }
          }}
        />
      )}
    </Stack>
  );
};

ReportStatusBadge.propTypes = {
  status: PropTypes.string,
  reportVersion: PropTypes.number,
};

/**
 * Main modal component for displaying workout session reports
 */
const WorkoutSessionReportModal = ({ open, onClose, session }) => {
  const theme = useTheme();

  if (!session) return null;

  const { 
    report, 
    isLoading, 
    isGenerating, 
    error,
    refetch 
  } = useSessionReport(session.id, session.status);

  const handleRetry = () => {
    if (refetch) {
      refetch();
    }
  };

  // Determine overall loading state
  const showLoading = isLoading || isGenerating || 
    (report && ['creating', 'waiting_for_completion'].includes(report.status));

  // Determine if we should show the report
  const showReport = report && report.status === 'complete' && !isLoading && !isGenerating;

  // Determine if there's an error
  const showError = (error || (report && report.status === 'failed')) && !isLoading;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md" 
      scroll="paper"
      PaperProps={{
        sx: {
          minHeight: '60vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 1.5
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            Smart Assessment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {session.name || 'Workout Session'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {report && (
            <ReportStatusBadge 
              status={report.status} 
              reportVersion={report.report_version}
            />
          )}
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {showLoading && (
          <ReportLoadingState 
            status={report?.status} 
            isGenerating={isGenerating}
          />
        )}
        
        {showError && (
          <ReportErrorState 
            error={report?.generation_error || error}
            onRetry={handleRetry}
          />
        )}
        
        {showReport && (
          <WorkoutSessionReport report={report} />
        )}

        {/* Edge case: No data at all */}
        {!showLoading && !showError && !showReport && (
          <Alert severity="info" sx={{ m: 2 }}>
            <Typography variant="body2">
              No report data is available for this session yet.
              {session.status !== 'COMPLETED' && (
                <> Complete your workout to generate a detailed assessment.</>
              )}
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {showReport && report?.ai_analysis?.session_quality_score > 0 && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Session Quality:
            </Typography>
            <Chip 
              label={`${report.ai_analysis.session_quality_score.toFixed(0)}/100`}
              size="small"
              color={
                report.ai_analysis.session_quality_score >= 80 ? 'success' :
                report.ai_analysis.session_quality_score >= 60 ? 'primary' : 'warning'
              }
            />
          </Box>
        )}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

WorkoutSessionReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  session: PropTypes.object,
};

export default WorkoutSessionReportModal;